import React, { FC, PropsWithChildren, ReactElement, ReactNode, useEffect, useState } from 'react';
import {
  Button,
  ButtonProps,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import { ModalConfig } from 'new/routes/RouterManager';
import { useModals } from 'new/hooks/useModals';
import CloseIcon from '@mui/icons-material/Close';
import styles from './ModalDefault.module.scss';
import { omit } from 'lodash';
import cx from 'classnames';
import classNames from 'classnames';

export interface ModalDefaultProps {
  open?: boolean;
  actions?: ModalActions;
  onConfirm?: () => void;
  onCancel?: (event: any, reason: any) => void;
  onEntering?: () => void;
  renderTitle?: (props: {
    Container: FC<PropsWithChildren<{ className?: string }>>;
    Title: FC<PropsWithChildren<{ className?: string }>>;
  }) => ReactNode;
  config?: {
    /**
     * disable the functionality to close the modal clicking out
     */
    disableClickOut?: boolean;
  };
}

export type ModalOutProps = {
  setModalActions: (actions: ModalActions) => void;
  setFooter: (Footer: ReactNode) => void;
  setFullWidth: (fullWidth: boolean) => void;
  setLoading: (loading: boolean) => void;
  setTitle: (title: string) => void;
};

function Loader({ isLoading }: { isLoading: boolean }) {
  if (!isLoading) {
    return null;
  }
  return (
    <Stack className={styles.loaderContainer}>
      <CircularProgress />
    </Stack>
  );
}

export type ModalActions = {
  ok: {
    text: string;
    color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
    onClick?: (onConfirm?: (...args: any[]) => void) => void;
  } & ButtonProps;
  cancel?: {
    text?: string;
    color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
    onClick?: (onCancel?: (...args: any[]) => void) => void;
  } & ButtonProps;
};

const TitleContainer = (props: PropsWithChildren<{ className?: string }>) => {
  const { children, className } = props;
  return <DialogTitle className={classNames(styles.title_view, className)}>{children}</DialogTitle>;
};

const Title = (props: PropsWithChildren<{ className?: string }>) => {
  const { children, className } = props;
  return <span className={classNames(styles.title, className)}>{children}</span>;
};

export function ModalDefault(props: PropsWithChildren<ModalDefaultProps & Partial<ModalConfig>>) {
  const {
    open = true,
    fullWidth: fw,
    maxWidth,
    name,
    onEntering,
    onConfirm,
    onCancel,
    children: Component,
    title: propTitle,
    rootClassName,
    contentClassName,
    removeContentPadding,
    renderTitle,
    ...rest
  } = props;
  const { disableClickOut = false } = props.config ?? {};

  const [fullWidth, setFullWidth] = useState(fw);
  const [stateTitle, setTitle] = useState(propTitle);
  const [stateActions, setModalActions] = useState<ModalActions>(props.actions);
  const [Footer, setFooter] = useState<ReactNode>(null);
  const [isLoading, setLoading] = useState(false);
  const { closeModal } = useModals();

  const actions = props.actions || stateActions;

  const onLocalCancel = (event, reason) => {
    if (actions?.cancel?.onClick) {
      actions?.cancel?.onClick(props.onCancel);
      return;
    }
    if (props.onCancel) {
      props.onCancel(event, reason);
    }
    closeModal(name);
  };

  const onLocalConfirm = () => {
    if (actions?.ok.onClick) {
      actions?.ok.onClick(props.onConfirm);
      return;
    }
    if (props.onConfirm) {
      props.onConfirm();
    }
    closeModal(name);
  };

  const onLocalClose = (event, reason) => {
    if (props.onCancel) {
      props.onCancel(event, reason);
    }
    closeModal(name);
  };

  return (
    <Dialog
      aria-label="default-modal"
      fullWidth={fullWidth}
      maxWidth={maxWidth || 'lg'}
      TransitionProps={{ onEntering }}
      onClose={disableClickOut ? undefined : onLocalClose}
      className={rootClassName}
      open={open}
      {...rest}
    >
      {!!renderTitle ? (
        renderTitle({ Container: TitleContainer, Title })
      ) : (
        <TitleContainer>
          <Title>{stateTitle}</Title>
          <div className={styles.actionClose}>
            <IconButton onClick={onLocalClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </TitleContainer>
      )}

      <DialogContent
        aria-label="modal-default-content"
        dividers
        className={cx({ [styles.removePadding]: removeContentPadding }, contentClassName)}
      >
        {React.cloneElement(Component as ReactElement, {
          setModalActions,
          setLoading,
          setFooter,
          setFullWidth,
          setTitle,
          inModal: true,
        })}
        <Loader isLoading={isLoading} />
      </DialogContent>

      {actions ? (
        <DialogActions className={styles.dialog_actions} aria-label="dialog-actions-container">
          <Button
            variant="outlined"
            data-testid="cancel-modal"
            color={actions?.cancel?.color || 'info'}
            {...omit(actions?.cancel, 'text', 'onClick')}
            onClick={onLocalCancel}
          >
            {actions?.cancel?.text || 'Cancel'}
          </Button>
          <Button
            data-testid="confirm-modal"
            variant="ghost"
            color={actions?.ok?.color || 'primary'}
            {...omit(actions?.ok, 'text', 'onClick')}
            onClick={onLocalConfirm}
          >
            {actions?.ok?.text || 'Ok'}
          </Button>
        </DialogActions>
      ) : (
        Footer
      )}
    </Dialog>
  );
}

ModalDefault.displayName = 'ModalDefault';
